// web/src/components/ReportView.tsx — one monthly report, phone-first (portal spec 17): the headline, then up to three
// cards ("Your customers", "Found on Google and Bing", "Kept safe and fast") of label → value lines, numbers right-aligned
// with their change vs last month; then "What we did" and "What we noticed". The words are the update brain's.
import type { ReportDoc, ReportLine } from '../lib/types';
import { Icon } from './Icon';

const DELTA_CLS: Record<NonNullable<ReportLine['delta']>, string> = {
  up: 'text-run', down: 'text-warn', flat: 'text-muted', new: 'text-info',
};

export function ReportView({ doc, label }: { doc: ReportDoc; label: string }) {
  return (
    <article className="flex min-w-0 flex-col gap-4">
      {doc.practiceData && (
        <p className="rounded-md border border-info/40 bg-surface px-3 py-2 text-xs text-muted">Practice data: your site doesn’t have real visitors yet.</p>
      )}
      <header>
        <div className="text-xs text-muted">{label}</div>
        <h2 className="mt-1 font-head text-lg text-ink">{doc.headline}</h2>
      </header>
      {doc.cards.map((c) => (
        <section key={c.key} className="rounded-lg border border-hair bg-surface p-4">
          <h3 className="mb-2 font-head text-sm text-muted">{c.title}</h3>
          <ul className="flex flex-col divide-y divide-hair">
            {c.lines.map((l, i) => (
              <li key={`${i}-${l.label}`} className="flex items-baseline justify-between gap-3 py-2">
                <span className="min-w-0">
                  <span className="block text-sm text-ink">{l.label}</span>
                  {l.note && <span className="block text-xs text-muted">{l.note}</span>}
                </span>
                <span className="max-w-[55%] shrink-0 break-words text-right">
                  <span className="font-head text-ink">{l.value}</span>
                  {l.deltaText && <span className={`ml-2 whitespace-nowrap text-xs ${DELTA_CLS[l.delta ?? 'flat']}`}>{l.deltaText}</span>}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ))}
      {doc.done.length > 0 && (
        <section className="rounded-lg border border-hair bg-surface p-4">
          <h3 className="mb-2 font-head text-sm text-muted">What we did</h3>
          <ul className="flex flex-col gap-1.5">
            {doc.done.map((d, i) => <li key={`${i}-${d}`} className="flex gap-2 text-sm text-ink"><Icon name="check" size={15} className="mt-0.5 shrink-0 text-run" />{d}</li>)}
          </ul>
        </section>
      )}
      {doc.noticed.length > 0 && (
        <section className="rounded-lg border border-hair bg-surface p-4">
          <h3 className="mb-2 font-head text-sm text-muted">What we noticed</h3>
          <ul className="flex list-disc flex-col gap-1.5 pl-5 text-sm text-ink">{doc.noticed.map((n, i) => <li key={`${i}-${n}`}>{n}</li>)}</ul>
        </section>
      )}
      {doc.screenshots.length > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {doc.screenshots.map((s) => (
            <figure key={s.url} className="overflow-hidden rounded-lg border border-hair bg-surface">
              <img src={s.url} alt={s.caption} loading="lazy" className="w-full" />
              <figcaption className="px-3 py-2 text-xs text-muted">{s.caption}</figcaption>
            </figure>
          ))}
        </div>
      )}
    </article>
  );
}

/** "October" for '2026-10' (the run month: "Your October update"). */
export const monthName = (p: string) => new Date(`${p}-15T12:00:00Z`).toLocaleString('en-US', { month: 'long', timeZone: 'UTC' });
/** "October 2026" for '2026-10'. */
export const monthLabel = (p: string) => new Date(`${p}-15T12:00:00Z`).toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const short = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
/** "Sep 21 – Oct 18" — the window the numbers cover. */
export const windowLabel = (r: { windowFrom: string; windowTo: string }) => `${short(r.windowFrom)} – ${short(r.windowTo)}`;
